type PlanTierKey = 'build' | 'essential' | 'professional' | 'enterprise'
type CloudProductKey = 'mmc' | 'mcc' | 'msc'

export type PurchaseChangeType = 'first_purchase' | 'upgrade'

const CLOUD_PRODUCT_PREFIX = /^(mmc|mcc|msc)[_\s]+/i

export const PLAN_TIER_RANK: Record<PlanTierKey, number> = {
  build: 1,
  essential: 2,
  professional: 3,
  enterprise: 4,
}

export function parsePackageTier(packageName: string): PlanTierKey | null {
  if (!packageName) return null

  const rest = packageName
    .toLowerCase()
    .trim()
    .replace(CLOUD_PRODUCT_PREFIX, '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (!rest || rest === 'free trial') return null
  if (rest === 'essentials' || rest === 'essential') return 'essential'
  if (rest === 'pro' || rest === 'professional') return 'professional'
  if (rest === 'build' || rest === 'enterprise') return rest

  return null
}

/**
 * Classifies a successful checkout for a product:
 * - cancelled (empty package_name) / free trial → paid plan → first_purchase
 * - paid plan → higher tier → upgrade (downgrades are not possible)
 */
export function getPurchaseChangeType(
  previousPlan: string,
  currentPlan: string,
  _productKey: CloudProductKey
): PurchaseChangeType | null {
  if (!currentPlan || currentPlan === previousPlan) return null

  const currentTier = parsePackageTier(currentPlan)
  if (!currentTier) return null

  // Cancelled packages have package_name: ""; free trial also has no paid tier
  const previousTier = parsePackageTier(previousPlan)
  if (!previousTier) {
    return 'first_purchase'
  }

  if (PLAN_TIER_RANK[currentTier] > PLAN_TIER_RANK[previousTier]) {
    return 'upgrade'
  }

  return null
}

/**
 * Detects a successful checkout for a product (first purchase or upgrade).
 */
export function hasPurchasedPlan(
  previousPlan: string,
  currentPlan: string,
  productKey: CloudProductKey
): boolean {
  return getPurchaseChangeType(previousPlan, currentPlan, productKey) !== null
}
